// src/services/apiClient.ts — Core request helpers and error handling with CSRF support
export const API_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// V1 routes are mounted at root /v1, not /api/v1
// Replace only the trailing /api, not the one in api.deploynowapp.online
export const V1_URL: string = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

// CSRF token storage
let csrfToken: string | null = null;

export const setCsrfToken = (token: string | null): void => {
    csrfToken = token;
};

export const getCsrfToken = (): string | null => {
    return csrfToken;
};

export class ApiError extends Error {
    status: number;
    data: unknown;

    constructor(status: number, message: string, data: unknown = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

export async function parseErrorResponse(res: Response): Promise<{ message: string; data: unknown }> {
    try {
        const data = await res.json();
        return {
            message: data.error || data.message || `Request failed with status ${res.status}`,
            data
        };
    } catch {
        const text = await res.text().catch(() => '');
        return {
            message: text || `Request failed with status ${res.status}`,
            data: null
        };
    }
}

export const apiClient = {
    setToken: (token: string | null): void => {
        if (token) localStorage.setItem('token', token);
        else localStorage.removeItem('token');
    },

    getHeaders: (): Record<string, string> => {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
        };
        
        // Add CSRF token for state-changing requests
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }
        
        return headers;
    },

    get: async <T = any>(path: string): Promise<T> => {
        const res = await fetch(`${API_URL}${path}`, { 
            headers: apiClient.getHeaders(),
            credentials: 'include' // Include cookies for CSRF
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    },

    getBlob: async (path: string): Promise<Blob> => {
        const res = await fetch(`${API_URL}${path}`, { 
            headers: apiClient.getHeaders(),
            credentials: 'include'
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.blob();
    },

    post: async <T = any>(path: string, body?: unknown): Promise<T> => {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'POST',
            headers: apiClient.getHeaders(),
            body: JSON.stringify(body),
            credentials: 'include' // Include cookies for CSRF
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    },

    put: async <T = any>(path: string, body?: unknown): Promise<T> => {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'PUT',
            headers: apiClient.getHeaders(),
            body: JSON.stringify(body),
            credentials: 'include' // Include cookies for CSRF
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    },

    patch: async <T = any>(path: string, body?: unknown): Promise<T> => {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'PATCH',
            headers: apiClient.getHeaders(),
            body: JSON.stringify(body),
            credentials: 'include'
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    },

    del: async <T = any>(path: string, body?: unknown): Promise<T> => {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'DELETE',
            headers: apiClient.getHeaders(),
            // A confirmation (e.g. the name of what is deleted) when the API asks for one.
            body: body === undefined ? undefined : JSON.stringify(body),
            credentials: 'include' // Include cookies for CSRF
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    },

    _agentGet: async <T = any>(path: string): Promise<T> => {
        const res = await fetch(`${V1_URL}${path}`, { 
            headers: apiClient.getHeaders(),
            credentials: 'include'
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    },

    _agentPost: async <T = any>(path: string, body?: unknown): Promise<T> => {
        const res = await fetch(`${V1_URL}${path}`, {
            method: 'POST',
            headers: apiClient.getHeaders(),
            body: JSON.stringify(body),
            credentials: 'include'
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    },

    _agentDel: async <T = any>(path: string): Promise<T> => {
        const res = await fetch(`${V1_URL}${path}`, {
            method: 'DELETE',
            headers: apiClient.getHeaders(),
            credentials: 'include'
        });
        if (!res.ok) {
            const { message, data } = await parseErrorResponse(res);
            throw new ApiError(res.status, message, data);
        }
        return res.json();
    }
};

export default apiClient;
